import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, PasswordInput } from '@mantine/core';
import PageHeader from '@/components/page-header';
import SettingsLayout from '@/components/settings-layout';
import { setWebTitle } from '@/lib/format-utils';
import { notify } from '@/lib/notify';
import api from '@/services/api-service';

export default function SettingsChangePasswordPage(): JSX.Element {
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    setWebTitle(t('change_password'));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!newPassword) {
            setError('new-password-required');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('passwords_do_not_match');
            return;
        }

        setIsLoading(true);
        try {
            const { data, error: apiError } = await api.auth.changePassword({
                currentPassword: currentPassword || undefined,
                newPassword,
            });

            if (apiError) {
                const errMsg = apiError.message || apiError || 'error';
                setError(errMsg);
                notify.error(t('error'), t(errMsg));
            } else {
                notify.success(t('success'), t('password_changed_successfully'));
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            }
        } catch (err: any) {
            const errMsg = err.message || 'error';
            setError(errMsg);
            notify.error(t('error'), t(errMsg));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <PageHeader title={t('settings')} />

            <div className="panel-content">
                <SettingsLayout>
                    <div className="max-w-md">
                        <div className="font-semibold text-gray-800 text-2xl mb-2">
                            {t('change_password')}
                        </div>
                        <div className="text-gray-700 text-sm mb-6">
                            {t('change_password_description')}
                        </div>

                        {error && (
                            <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 border border-red-200">
                                {t(error)}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <PasswordInput
                                label={t('current_password')}
                                placeholder="••••••••"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                size="md"
                            />

                            <PasswordInput
                                label={t('new_password')}
                                placeholder="••••••••"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                size="md"
                            />

                            <PasswordInput
                                label={t('confirm_password')}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                size="md"
                            />

                            <div className="pt-2">
                                <Button
                                    type="submit"
                                    color="black"
                                    size="md"
                                    loading={isLoading}
                                >
                                    {t('change_password')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </SettingsLayout>
            </div>
        </div>
    );
}
